import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Bell, Check, LifeBuoy, Loader2, Wallet, X } from "lucide-react";
import { api, dt, errMsg, rp } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/** Bar notifikasi header: permintaan saldo (prioritas merah) + tiket gangguan. */
export const NotifBar = ({ refreshKey = 0 }) => {
  const [deposits, setDeposits] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [busyId, setBusyId] = useState("");

  const load = async () => {
    const [d, t] = await Promise.allSettled([api.get("/deposits?status=pending"), api.get("/tickets?status=buka")]);
    if (d.status === "fulfilled") setDeposits(d.value.data);
    if (t.status === "fulfilled") setTickets(t.value.data);
  };

  useEffect(() => {
    load();
    const i = setInterval(load, 20000);
    return () => clearInterval(i);
  }, [refreshKey]);

  const total = useMemo(() => deposits.length + tickets.length, [deposits, tickets]);

  const act = async (d, action) => {
    const label = action === "approve" ? "Setujui" : "Tolak";
    if (!window.confirm(`${label} permintaan saldo ${d.username} sebesar ${rp(d.amount)}?`)) return;
    setBusyId(d.id);
    try {
      await api.post(`/deposits/${d.id}/action`, { action, note: `${label} dari panel notifikasi` });
      toast.success(action === "approve" ? `Saldo ${d.username} ditambah ${rp(d.amount)}` : `Permintaan ${d.username} ditolak`);
      setDeposits((prev) => prev.filter((x) => x.id !== d.id));
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusyId(""); }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="notif-bar">
          <Bell className="h-5 w-5" />
          {deposits.length > 0 && (
            <span
              className="absolute -right-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-red-600 px-1 text-[9px] font-bold text-white ring-2 ring-background"
              data-testid="notif-saldo-badge"
            >
              {deposits.length}
            </span>
          )}
          {!deposits.length && tickets.length > 0 && (
            <span
              className="absolute -right-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-sky-500 px-1 text-[9px] font-bold text-white ring-2 ring-background"
              data-testid="notif-ticket-badge"
            >
              {tickets.length}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[340px] p-0" data-testid="notif-panel">
        <div className="border-b border-border px-4 py-3">
          <p className="font-display text-sm font-semibold">Notifikasi ({total})</p>
          <p className="text-[11px] text-muted-foreground">Permintaan saldo diberi tanda merah (prioritas)</p>
        </div>
        <div className="max-h-[320px] space-y-1 overflow-auto p-2 scrollbar-thin">
          {deposits.map((d) => (
            <div key={d.id} className="rounded-xl border border-red-500/40 bg-red-500/10 px-3 py-2" data-testid={`notif-deposit-${d.username}`}>
              <div className="flex items-center justify-between gap-2">
                <Link to={d.role === "user" ? "/user" : "/mitra"} className="flex items-center gap-2 text-xs font-medium hover:underline">
                  <Wallet className="h-3.5 w-3.5 text-red-400" /> {d.username}
                </Link>
                <span className="font-mono text-xs text-red-300">{rp(d.amount)}</span>
              </div>
              <p className="mt-0.5 text-[10px] text-muted-foreground">Permintaan saldo · {dt(d.created_at)}</p>
              <div className="mt-2 flex gap-2">
                <Button
                  size="sm" className="h-7 flex-1 rounded-full bg-emerald-600 text-[11px] text-white" disabled={busyId === d.id}
                  onClick={() => act(d, "approve")} data-testid={`notif-approve-${d.username}`}
                >
                  {busyId === d.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Check className="mr-1 h-3.5 w-3.5" /> Setujui</>}
                </Button>
                <Button
                  size="sm" variant="outline" className="h-7 flex-1 rounded-full text-[11px] text-red-400" disabled={busyId === d.id}
                  onClick={() => act(d, "reject")} data-testid={`notif-reject-${d.username}`}
                >
                  <X className="mr-1 h-3.5 w-3.5" /> Tolak
                </Button>
              </div>
            </div>
          ))}
          {tickets.map((t) => (
            <Link
              key={t.id} to="/tiket" data-testid={`notif-ticket-${t.code}`}
              className="block rounded-xl border border-border px-3 py-2 transition-colors hover:bg-secondary/50"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-2 text-xs font-medium"><LifeBuoy className="h-3.5 w-3.5 text-sky-400" /> {t.customer_name}</span>
                <span className="font-mono text-[10px] text-muted-foreground">{t.code}</span>
              </div>
              <p className="mt-0.5 text-[10px] text-muted-foreground">Tiket {t.category} · {t.status} · {dt(t.created_at)}</p>
            </Link>
          ))}
          {!total && <p className="py-8 text-center text-xs text-muted-foreground">Tidak ada notifikasi baru</p>}
        </div>
      </PopoverContent>
    </Popover>
  );
};
